<template>
  <div class="focus-timer">
    <button
      class="ft-btn"
      :title="running ? 'Pause focus timer' : 'Start focus timer'"
      @click="toggle"
    >
      <span class="material-symbols-rounded ft-icon">
        {{ running ? 'pause_circle' : 'play_circle' }}
      </span>
      <span
        v-if="running"
        class="ft-time"
      >
        {{ display }}
      </span>
    </button>

    <div class="ft-duration-wrap">
      <button
        class="ft-btn ft-btn--text"
        :title="`Change duration (${durationMins} min)`"
        @click="pickerOpen = !pickerOpen"
      >
        <span class="ft-duration-label">{{ durationMins }} min</span>
        <span class="ft-duration-label-compact">{{ durationMins }}</span>
        <span class="material-symbols-rounded ft-chevron">expand_more</span>
      </button>
      <div
        v-if="pickerOpen"
        v-click-outside="() => pickerOpen = false"
        class="duration-menu"
      >
        <button
          v-for="min in DURATIONS"
          :key="min"
          class="btn-tertiary duration-item"
          @click="onPick(min)"
        >
          {{ min }} min
        </button>
      </div>
    </div>

    <button
      class="ft-btn"
      title="Reset timer"
      @click="reset"
    >
      <span class="material-symbols-rounded ft-icon">restart_alt</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, type DirectiveBinding } from "vue"
import { useFocusTimer } from "@/composables/useFocusTimer"

const DURATIONS = [ 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60 ]

const { running, durationMins, display, toggle, reset, setDuration } = useFocusTimer()
const pickerOpen = ref(false)

function onPick(min: number) {
  setDuration(min)
  pickerOpen.value = false
}

const vClickOutside = {
  mounted(el: HTMLElement, binding: DirectiveBinding<() => void>) {
    const handler = (e: Event) => {
      if (!el.contains(e.target as Node)) binding.value()
    }
    ;(el as HTMLElement & { _clickOutside?: typeof handler })._clickOutside = handler
    setTimeout(() => document.addEventListener("click", handler), 0)
  },
  unmounted(el: HTMLElement) {
    const handler = (el as HTMLElement & { _clickOutside?: (e: Event) => void })._clickOutside
    if (handler) document.removeEventListener("click", handler)
  },
}
</script>

<style scoped>
.focus-timer {
  display: flex;
  align-items: center;
  gap: 0.125rem;
  height: 2.25rem;
  min-width: 0;
}

/* Default: show the full "25 min" label, hide the compact form */
.ft-duration-label-compact { display: none; }

/* Uniform-height buttons so the icons and text all sit on one line. */
.ft-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  height: 2.25rem;
  padding: 0 0.5rem;
  border-radius: 0.5rem;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--color-primary);
  font-family: 'Manrope', sans-serif;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
  transition: background-color 0.15s;
}
.ft-btn:hover {
  background-color: var(--color-surface-container-high);
}

.ft-btn--text {
  gap: 0.125rem;
  padding: 0 0.375rem;
}

.ft-icon {
  font-size: 1.125rem;
  line-height: 1;
}

.ft-time {
  font-variant-numeric: tabular-nums;
  min-width: 2.75rem;
  line-height: 1;
}

.ft-chevron {
  font-size: 0.875rem;
  opacity: 0.6;
  line-height: 1;
}

.ft-duration-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 100%;
}

.duration-menu {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-surface-container-lowest);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  overflow-y: auto;
  max-height: 14rem;
  min-width: 6rem;
  z-index: 200;
  display: flex;
  flex-direction: column;
}
.duration-item {
  border-radius: 0;
  padding: 0.5rem 1rem;
  justify-content: center;
  font-family: 'Manrope', sans-serif;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-on-surface);
}

/* Narrow phones: drop the chevron + the running-time readout so the
   header doesn't overflow. The duration label stays visible as the
   primary control. */
@media (max-width: 420px) {
  .ft-btn {
    padding: 0 0.375rem;
  }
  .ft-time,
  .ft-chevron {
    display: none;
  }
}

/* Very narrow (<= 340px): swap the "25 min" label for a compact "25"
   so the cluster keeps every control reachable at one-handed widths. */
@media (max-width: 340px) {
  .ft-btn {
    padding: 0 0.25rem;
    font-size: 0.75rem;
  }
  .ft-duration-label { display: none; }
  .ft-duration-label-compact { display: inline; }
}
</style>
